import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LogOut, Menu, X } from "lucide-react";
import TreeMark from "@/components/TreeMark";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);

  const links = [
    { to: "/guide/bereaved", label: "If you've been bereaved" },
    { to: "/guide/prepare", label: "Prepare for family" },
    { to: "/about", label: "About" },
    ...(user ? [
      { to: "/plan", label: "My Plan" },
      { to: "/chat", label: "Ask" },
      { to: "/dashboard", label: "Dashboard" },
    ] : []),
    ...(user?.role === "admin" ? [{ to: "/admin", label: "Admin" }] : []),
  ];

  return (
    <nav
      data-testid="navbar"
      className="sticky top-0 z-40 bg-[#FAF9F6]/95 backdrop-blur-md border-b border-[#EAE6E1]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between gap-6">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-2.5 md:gap-3 min-w-0 group flex-shrink-0">
          <TreeMark size={40} tone="dark" className="flex-shrink-0 shadow-sm transition-transform group-hover:scale-105 md:hidden" />
          <TreeMark size={48} tone="dark" className="flex-shrink-0 shadow-sm transition-transform group-hover:scale-105 hidden md:inline-block" />
          <span
            className="font-serif text-[#2D332D] leading-none tracking-tight whitespace-nowrap"
            style={{ fontSize: "clamp(19px, 3.6vw, 26px)" }}
          >
            At The End of The Day
          </span>
          <span className="text-[10px] text-[#8A9488] tracking-[0.2em] uppercase hidden xl:inline">UK</span>
        </Link>

        <div className="hidden md:flex items-center gap-0.5 flex-shrink-0">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.replaceAll('/', '-')}`}
              className={({ isActive }) =>
                `px-2.5 lg:px-3 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                  isActive ? "bg-[#E9ECE8] text-[#2D332D]" : "text-[#576056] hover:bg-[#F3F1ED]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          {user ? (
            <>
              {!user.is_premium && (
                <Link
                  to="/upgrade"
                  data-testid="nav-upgrade"
                  className="ml-2 px-3.5 py-2 rounded-full text-sm bg-[#2D332D] text-[#F5ECE7] hover:bg-[#3D453D] transition-colors whitespace-nowrap"
                >
                  Upgrade · £29
                </Link>
              )}
              <button
                onClick={async () => { await logout(); navigate("/"); }}
                data-testid="logout-btn"
                className="ml-1 px-3 py-2 rounded-full text-sm text-[#6B7F62] hover:bg-[#E9ECE8] transition-colors whitespace-nowrap inline-flex items-center gap-1.5"
              >
                <LogOut size={15} /> Sign out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="btn-ghost text-sm ml-1 whitespace-nowrap">Sign in</Link>
              <Link to="/register" data-testid="nav-register" className="btn-primary text-sm ml-1 whitespace-nowrap">Get started</Link>
            </>
          )}
        </div>

        <button
          className="md:hidden p-2 text-[#2D332D]"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#EAE6E1] bg-[#FAF9F6] px-6 py-4 space-y-1">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              data-testid={`mnav-${l.to.replaceAll('/', '-')}`}
              className="block py-2.5 text-[#2D332D]"
            >{l.label}</Link>
          ))}
          {user ? (
            <>
              {!user.is_premium && (
                <Link to="/upgrade" onClick={() => setOpen(false)} data-testid="mnav-upgrade" className="block py-2.5 text-[#2D332D] font-medium">Upgrade · £29</Link>
              )}
              <button
                onClick={async () => { setOpen(false); await logout(); navigate("/"); }}
                className="block py-2.5 text-[#B05A5A]"
                data-testid="mobile-logout"
              >Sign out</button>
            </>
          ) : (
            <div className="pt-2 space-y-2">
              <Link to="/login" onClick={() => setOpen(false)} data-testid="mnav-login" className="block py-2.5 text-[#2D332D]">Sign in</Link>
              <Link to="/register" onClick={() => setOpen(false)} data-testid="mnav-register" className="btn-primary w-full justify-center">Get started</Link>
            </div>
          )}
        </div>
      )}
    </nav>
  );
}
